/**
 * **Ending a coaching relationship, from the client's own card.**
 *
 * Built 16 September 2026. The act existed already, on the roster preview panel's `Więcej` menu,
 * and the owner took that menu off — *"te widoki sa read only praktycznie"*. It could not simply be
 * deleted: the panel's menu was the ONLY route to this endpoint in the whole web app, and the
 * client detail's own header carries a note saying the `⋯` KANON v3 draws there was left unbuilt
 * *because* the roster already had it. That reason expired, so the menu is built where the canon
 * put it.
 *
 * A `features/` slice rather than a second copy inside `pages/client-detail`, because the roster
 * still performs this act from its seat-limit panel: a trainer at the cap frees a seat by ending a
 * relationship. Two callers, one endpoint, one set of consequences.
 *
 * What it does NOT own: what the act means afterwards (`entities/client`'s `clientActionOutcome`)
 * and the sentence it has to answer for (`clientConfirmPrompt`). Both moved to the entity in this
 * round so the roster and this slice read one copy — a duplicated `session_in_progress` rule is a
 * rule that will eventually disagree with itself.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  archivedClientsQueryKey,
  rosterQueryKey,
  rowActionFailed,
  rowActionSucceeded,
  seatUsageQueryKey,
  type RosterCache,
} from '~/entities/client';
import { postCustomerIdArchive } from '~/shared/api/generated/endpoints/customer/customer';
import { ApiError } from '~/shared/api/mutator';

export interface EndCoaching {
  /** The confirmation is open. The dialog is the only thing that reads this. */
  readonly confirming: boolean;
  /** The write is in flight, so the dialog holds its own busy state rather than closing early. */
  readonly busy: boolean;
  /**
   * Why the server refused, or `null`. The dialog STAYS OPEN and says this in place of its own
   * description.
   *
   * ── Why it is not announced into a live region ──────────────────────────────────────────
   * The roster says its refusals that way, and copying that here put a SECOND `role="status"` on
   * this screen: the client's plan calendar is a tab of this same screen and already owns one. Two
   * live regions on one screen is an accessibility defect, not a test problem — caught by
   * `assigned-plan-calendar.spec.ts`, whose own status assertions went ambiguous the moment this
   * feature mounted.
   *
   * Saying it in the dialog is the better answer regardless of the collision. `session_in_progress`
   * is a refusal the trainer can ACT on — finish or cancel the session you are running with this
   * client — and an instruction belongs where the eyes already are and where the failed act is
   * still on screen, not in a region a sighted trainer never sees.
   */
  readonly refusal: string | null;
  readonly ask: () => void;
  readonly dismiss: () => void;
  readonly confirm: () => void;
}

/**
 * @param customerId the client whose relationship ends. `null` is refused rather than defaulted:
 *   a `?? 0` here is what once posted to `/customer/0/archive`, which 404s while looking like a
 *   well-formed request. See `rowActionOutcome`'s note on the same bug.
 * @param onEnded run after the server accepts. The detail screen leaves for the roster, because
 *   the client it is showing no longer belongs to this trainer.
 */
export function useEndCoaching(customerId: number | null, onEnded: () => void): EndCoaching {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const archive = useMutation({
    mutationFn: (id: number) => postCustomerIdArchive(id),
  });

  /** The one place a cache NAME becomes a cache KEY. The model names caches; `api/` owns keys. */
  const CACHE_KEYS: Readonly<Record<RosterCache, readonly unknown[]>> = {
    roster: rosterQueryKey,
    seats: seatUsageQueryKey,
    archive: archivedClientsQueryKey,
  };

  const invalidate = (outcome: ReturnType<typeof rowActionSucceeded>) => {
    for (const cache of outcome.invalidates) {
      void queryClient.invalidateQueries({ queryKey: CACHE_KEYS[cache] });
    }
  };

  const refuse = (status: number, code: string | null | undefined) => {
    // `clientActionOutcome` is what knows that `session_in_progress` has its own sentence rather
    // than the generic failure. The dialog stays open around it.
    setRefusal(t(rowActionFailed('endCoaching', status, code).announceKey));
  };

  return {
    confirming,
    busy: archive.isPending,
    refusal,
    ask: () => {
      setRefusal(null);
      setConfirming(true);
    },
    dismiss: () => {
      setRefusal(null);
      setConfirming(false);
    },
    confirm: () => {
      if (customerId === null) {
        // Answering a press by doing nothing is the one outcome the trainer cannot read.
        refuse(0, undefined);
        return;
      }
      void (async () => {
        try {
          await archive.mutateAsync(customerId);
          invalidate(rowActionSucceeded('endCoaching'));
          setConfirming(false);
          onEnded();
        } catch (error: unknown) {
          const status = error instanceof ApiError ? error.status : 0;
          const code = error instanceof ApiError ? error.code : undefined;
          refuse(status, code);
        }
      })();
    },
  };
}
